import { useEffect, useRef, useState } from 'react';
import { validateRequired, validatePercentage, ErrorMessage } from '../../utlis/function.js';
import CommonLoader from '../common/CommonLoader.jsx';

const EditAgeDistributionModal = ({ onClose, onSave, initialData = {}, isLoading }) => {
  const [errors, setErrors] = useState({});
  const initialized = useRef(false);
 const [formData, setFormData] = useState({
    underAge18: '',
    age18to24: '',
    age25to34: '',
    age35to44: '',
    age45to54: '',
    age55to64: '',
    age65plus: ''
  });

  useEffect(() => {
    if (!initialized.current && Object.keys(initialData).length > 0) {
      setFormData({
        underAge18: initialData.underAge18 || '',
        age18to24: initialData.age18to24 || '',
        age25to34: initialData.age25to34 || '',
        age35to44: initialData.age35to44 || '',
        age45to54: initialData.age45to54 || '',
        age55to64: initialData.age55to64 || '',
        age65plus: initialData.age65plus || ''
      });

      initialized.current = true;
    }
  }, [initialData]);

  const handleInputChange = (field, value) => {
    // Only allow numbers and decimal points
    const numericValue = value.replace(/[^0-9.]/g, '');
    setFormData(prev => ({
      ...prev,
      [field]: numericValue
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

 const validateField = (field, value) => {
  if (value === '' || value === null || value === undefined) {
    return `${getFieldLabel(field)} is required`;
  }

  if (!validatePercentage(value)) {
    return 'Please enter a valid percentage (0-100)';
  }

  return '';
};


  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const getFieldLabel = (field) => {
    const labels = {
      underAge18: 'Under age 18',
      age18to24: '18-24',
      age25to34: '25-34',
      age35to44: '35-44',
      age45to54: '45-54',
      age55to64: '55-64',
      age65plus: 'Age 65+'
    };
    return labels[field] || field;
  };

  const handleSave = async() => {
    if (validateForm()) {
      if (onSave) {
        await  onSave(formData);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-[#000000b5] flex items-center justify-center z-50">
      {isLoading && <CommonLoader fullScreen/>}
      <div className="bg-white rounded-[20px] max-w-[624px] w-full mx-4 relative overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Content */}
        <div className="p-8">
          {/* Title */}
          <h2 className="text-[30px] font-semibold text-black mb-2 tracking-[0] leading-[normal]">
            Age Distribution
          </h2>

          {/* Description */}
          <p className="text-[20px] font-normal text-[#666666] mb-8">
            Enter the percentage breakdown of the average audience by age group.
          </p>

          {/* Form Grid - Two columns */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            {/* Under age 18 */}
            <div>
              <label className="block text-[16px] font-medium text-black mb-3">
                Under age 18
              </label>
              <input
                type="text"
                value={formData.underAge18}
                 maxLength={30}
                onChange={(e) => handleInputChange('underAge18', e.target.value)}
                placeholder="0.5"
                className="w-full h-[48px] px-4 border-1 border-[#C5C5C5] rounded-full text-base font-normal text-black placeholder-[#999999] focus:outline-none focus:border-[#0A5F73] transition-colors"
              />
              <ErrorMessage message={errors.underAge18} />
            </div>

            {/* 18-24 */}
            <div>
              <label className="block text-[16px] font-medium text-black mb-3">
                18-24
              </label>
              <input
                type="text"
                value={formData.age18to24}
                 maxLength={30}
                onChange={(e) => handleInputChange('age18to24', e.target.value)}
                placeholder="0.5"
                className="w-full h-[48px] px-4 border-1 border-[#C5C5C5] rounded-full text-base font-normal text-black placeholder-[#999999] focus:outline-none focus:border-[#0A5F73] transition-colors"
              />
              <ErrorMessage message={errors.age18to24} />
            </div>

            {/* 25-34 */}
            <div>
              <label className="block text-[16px] font-medium text-black mb-3">
                25-34
              </label>
              <input
                type="text"
                value={formData.age25to34}
                 maxLength={30}
                onChange={(e) => handleInputChange('age25to34', e.target.value)}
                placeholder="0.5"
                className="w-full h-[48px] px-4 border-1 border-[#C5C5C5] rounded-full text-base font-normal text-black placeholder-[#999999] focus:outline-none focus:border-[#0A5F73] transition-colors"
              />
              <ErrorMessage message={errors.age25to34} />
            </div>

            {/* 35-44 */}
            <div>
              <label className="block text-[16px] font-medium text-black mb-3">
                35-44
              </label>
              <input
                type="text"
                value={formData.age35to44}
                 maxLength={30}
                onChange={(e) => handleInputChange('age35to44', e.target.value)}
                placeholder="0.5"
                className="w-full h-[48px] px-4 border-1 border-[#C5C5C5] rounded-full text-base font-normal text-black placeholder-[#999999] focus:outline-none focus:border-[#0A5F73] transition-colors"
              />
              <ErrorMessage message={errors.age35to44} />
            </div>

            {/* 45-54 */}
            <div>
              <label className="block text-[16px] font-medium text-black mb-3">
                45-54
              </label>
              <input
                type="text"
                value={formData.age45to54}
                onChange={(e) => handleInputChange('age45to54', e.target.value)}
                placeholder="0.5"
                 maxLength={30}
                className="w-full h-[48px] px-4 border-1 border-[#C5C5C5] rounded-full text-base font-normal text-black placeholder-[#999999] focus:outline-none focus:border-[#0A5F73] transition-colors"
              />
              <ErrorMessage message={errors.age45to54} />
            </div>

            {/* 55-64 */}
            <div>
              <label className="block text-[16px] font-medium text-black mb-3">
                55-64
              </label>
              <input
                type="text"
                value={formData.age55to64}
                 maxLength={30}
                onChange={(e) => handleInputChange('age55to64', e.target.value)}
                placeholder="0.5"
                className="w-full h-[48px] px-4 border-1 border-[#C5C5C5] rounded-full text-base font-normal text-black placeholder-[#999999] focus:outline-none focus:border-[#0A5F73] transition-colors"
              />
              <ErrorMessage message={errors.age55to64} />
            </div>

            {/* Age65+ - spans full width */}
            <div className="col-span-2">
              <label className="block text-[16px] font-medium text-black mb-3">
                Age65+
              </label>
              <input
                type="text"
                value={formData.age65plus}
                onChange={(e) => handleInputChange('age65plus', e.target.value)}
                maxLength={30}
                placeholder="0.5"
                className="w-full h-[48px] px-4 border-1 border-[#C5C5C5] rounded-full text-base font-normal text-black placeholder-[#999999] focus:outline-none focus:border-[#0A5F73] transition-colors"
              />
              <ErrorMessage message={errors.age65plus} />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="cursor-pointer flex-1 h-[56px] border-1 border-[#06323A] text-[#06323A] bg-white rounded-full text-base font-medium tracking-[0] leading-[normal] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="cursor-pointer flex-1 h-[56px] bg-[#0A3A44] text-white rounded-full text-base font-medium tracking-[0] leading-[normal] hover:bg-opacity-90 transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAgeDistributionModal;
